import React, { useState, useContext } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import toast from "react-hot-toast";
import loginImage from "../Assets/login.json";
import { ImageContext } from "../page/ApiContext.jsx";

const schema1 = z.object({
  email: z
    .string()
    .nonempty({ message: "Email is required" })
    .email({ message: "Enter valid email address" })
    .refine((email) => email.includes("sciative") && !email.includes("gmail"), {
      message: "Email must include 'sciative' and not 'gmail'",
    }),
  password: z
    .string()
    .nonempty({ message: "Password is required" })
    .min(6, { message: "Password must be at least 6 characters long" }),
});

const schema2 = z.object({
  email: z
    .string()
    .nonempty({ message: "Email is required" })
    .email({ message: "Enter valid email address" })
    .refine((email) => email.includes("sciative") && !email.includes("gmail"), {
      message: "Email must include 'sciative' and not 'gmail'",
    })
 
});

function Login() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [requestReset, setRequestReset] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const {
    setRowId,
    dashboardIds,
    setDashboardIds,
    functionalityIds,
    setFunctionalityIds,
    userRoleId,
    setUserRoleId,
    roleActive,
    setRoleActive,
  } = useContext(ImageContext);

  const {//for Login component form validation
    register : register1,
    handleSubmit : handleSubmit1,
    formState: { errors : errors1 },
    reset : reset1,
  } = useForm({
    resolver: zodResolver(schema1),
  });

  const {//for Reset password request component form validation
    register : register2,
    handleSubmit : handleSubmit2,
    formState: { errors : errors2 },
    reset : reset2,
  } = useForm({
    resolver: zodResolver(schema2),
  });

  const setCookie = (name, value, expiresInSeconds) => {
    const d = new Date();
    d.setTime(d.getTime() + expiresInSeconds * 1000);
    const expires = "expires=" + d.toUTCString();
    document.cookie = name + "=" + value + ";" + expires + ";path=/";
  };

  async function login(data) {
    const url = "/sqa/auth/sqa/token";

    const encodedEmail = btoa(data.email);
    const encodedPassword = btoa(data.password);

    const payload = {
      email: encodedEmail,
      password: encodedPassword,
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        if (response.status === 401) {
          toast.error("Incorrect email or password");
        }
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      if (data.error_list[0]?.status_code === 401) {
        toast.error("Incorrect email or password");
      }
      const { access_token, email, is_admin, role_id } = data.result[0];
      localStorage.setItem("email", email);
      localStorage.setItem("is_admin", is_admin);

      if (access_token) {
        setCookie("sqa_token", access_token, 86400);
        navigate("/");
        toast.success("Logged In successfully");
        setRoleActive(true);
      } else {
        toast.error("Please try again");
        navigate("/auth/user-info");
      }
    } catch (error) {
      console.error("There was an error with the fetch operation:", error);
    }
  }

  async function handlePasswordReset(data) {
    setDisabled(true); // Disable the button
    setTimeout(() => setDisabled(false), 5000);
    const url = "/sqa/api/reset_pwd_req/";  // Update this with your reset password endpoint

    const encodedEmail = btoa(data.email); // Base64 encode the email

    const payload = {
      email: encodedEmail,
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();

      if (responseData.success === false) {
        toast.error(responseData.message);
      } else {
        // setDisabled(true); // Disable the button
        // setTimeout(() => setDisabled(false), 5000);
        toast.success(responseData.message);
      }

    } catch (error) {
      console.error("Error with the fetch operation:", error);
      toast.error(error.message || "An error occurred. Please try again.");
    }
  }

  const handleClick = () => {
    setDisabled(true); // Disable button immediately
  
    setTimeout(() => {
      setDisabled(false); // Re-enable button after 5 seconds
    }, 5000);
  };

  return (
    <div className="flex min-h-screen">
      {/* Left side - Image */}
      <div className="hidden sm:flex sm:w-1/2 md:w-1/3 lg:w-1/2 h-screen justify-center items-center">
        <div className="w-3/4 h-3/4 flex justify-center items-center">
        <img
            src={loginImage.base64Image}
            alt="Login visual"
            className="max-w-full max-h-full object-contain"
          />

        </div>
      </div>

      {/* Right side - Login form */}
      {!requestReset && (<div className="w-full sm:w-1/2 md:w-2/3 lg:w-1/2 h-screen flex flex-col justify-center px-6 py-12 lg:px-8">
        <div className="text-3xl font-bold text-center w-full pb-2">
          Welcome Back
        </div>
        <div className="border-b-4 border-[#4946ed] w-[20%] rounded-md mx-auto text-center"></div>

        <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-sm bg-white bg-opacity-50  rounded-lg p-6 ">
          <form className="space-y-6" onSubmit={handleSubmit1(login)}>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Email Address <span className="text-red-500">*</span>
              </label>
              <div className="mt-2">
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  {...register1("email")}
                  className="block p-2 w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
                {errors1.email && (
                  <p className="mt-2 text-sm text-red-600">
                    {errors1.email.message}
                  </p>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Password <span className="text-red-500">*</span>
                </label>
              </div>
              <div className="mt-2 relative">
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    {...register1("password")}
                    className="block p-2 w-full rounded-md border-0 py-1.5 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
                  >
                    {showPassword ? (
                      <RemoveRedEyeIcon />
                    ) : (
                      <VisibilityOffIcon />
                    )}
                  </button>
                </div>
                {errors1.password && (
                  <p className="mt-2 text-sm text-red-600">
                    {errors1.password.message}
                  </p>
                )}
              </div>
            </div>

            <div>
              <button
                type="submit"
                className="flex w-full justify-center rounded-md bg-[#4946ed] px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >
                Sign in
              </button>
            </div>

            <div>
              <button
                type="button"
                onClick={() => {reset1();setRequestReset(true)}}
                className="flex w-full justify-center rounded-md border border-[#4946ED] px-3 py-1.5 text-sm font-semibold leading-6 text-[#4946ED] shadow-sm hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4946ED]"              >
                Reset Password
              </button>
            </div>
          </form>
        </div>
      </div>)}

      {requestReset && (<div className="w-full sm:w-1/2 md:w-2/3 lg:w-1/2 h-screen flex flex-col justify-center px-6 py-12 lg:px-8">
        <div className="text-3xl font-bold text-center w-full pb-2">
          Request Password Reset
        </div>
        <div className="border-b-4 border-[#4946ed] w-[20%] rounded-md mx-auto text-center"></div>

        <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-sm bg-white bg-opacity-50 rounded-lg p-6">
          <form className="space-y-6" onSubmit={handleSubmit2(handlePasswordReset)}>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Email Address <span className="text-red-500">*</span>
              </label>
              <div className="mt-2">
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  {...register2("email")}
                  className="block p-2 w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
                {errors2.email && (
                  <p className="mt-2 text-sm text-red-600">
                    {errors2.email.message}
                  </p>
                )}
              </div>
            </div>
          <div>
            <div>
              {/* <button
                type="submit"
                className="flex w-full justify-center rounded-md bg-[#4946ed] px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >
                Request Password Reset
              </button> */}
              <button
              type="submit"
              disabled={disabled}
              // onClick={handleClick}
              className={`flex w-full justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 shadow-sm ${
                disabled
                  ? "cursor-not-allowed flex w-full justify-center rounded-md border border-[#4946ED] px-3 py-1.5 text-sm font-semibold text-[#4946ED] shadow-sm hover:bg-gray-100"
                  : "bg-[#4946ed] text-white hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              }`}
            >
              {disabled ? "Please wait..." : "Request Password Reset"}
            </button>  
              
            </div>
            <div className="mt-4">
              <button
                type="button"
                onClick={() => {reset2();setRequestReset(false);}}
                className="flex w-full justify-center rounded-md border border-[#4946ED] px-3 py-1.5 text-sm font-semibold text-[#4946ED] shadow-sm hover:bg-gray-100"
              >
                Go to Login
              </button>
            </div>
          </div>
          </form>
        </div>
      </div>

      )}
    </div>
  );
}

export default Login;
